import { Trans, useLingui } from '@lingui/react/macro';
import { HourglassTop, Radar, Refresh } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  TableContainer,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { prettifySnakeCaseString } from '@tunarr/shared/util';
import type { MediaSourceLibrary, MediaSourceSettings } from '@tunarr/types';
import type { ScanProgress } from '@tunarr/types/api';
import { capitalize, maxBy, some } from 'lodash-es';
import type { MRT_ColumnDef } from 'material-react-table';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { useCallback, useMemo, useState } from 'react';
import {
  getApiMediaLibrariesByLibraryIdQueryKey,
  getApiMediaSourcesByMediaSourceIdByLibraryIdStatusOptions,
  getApiMediaSourcesQueryKey,
} from '../generated/@tanstack/react-query.gen.ts';
import {
  useLibraryScanState,
  useScanLibraryMutation,
} from '../hooks/media-sources/mediaSourceLibraryHooks.ts';
import { useMediaSources } from '../hooks/settingsHooks.ts';
import { useDayjs } from '../hooks/useDayjs.ts';
import { useQueryObserver } from '../hooks/useQueryObserver.ts';
import { useStoreBackedTableSettings } from '../hooks/useTableSettings.ts';
import type { Nullable } from '../types/util.ts';
import { NetworkIcon } from './util/NetworkIcon.tsx';

type MediaSourceLibraryRow = MediaSourceLibrary & {
  mediaSource: MediaSourceSettings;
};

type ActionCellProps = {
  mediaSource: MediaSourceSettings;
  library: MediaSourceLibraryRow;
};

const MediaSourceLibraryTableActionCell = ({
  mediaSource,
  library,
}: ActionCellProps) => {
  const { t } = useLingui();
  const [isRefreshing, setIsRefreshing] = useState(library.isLocked);
  const refreshLibraryMutation = useScanLibraryMutation();
  const scanStateQuery = useLibraryScanState(
    mediaSource.id,
    library.id,
    isRefreshing,
  );
  const [prevScanState, setPrevScanState] =
    useState<Nullable<ScanProgress['state']>>(null);

  const queryClient = useQueryClient();

  const startRefresh = useCallback(
    (ev: React.MouseEvent, force: boolean = false) => {
      ev.stopPropagation();
      ev.preventDefault();
      setIsRefreshing(true);
      refreshLibraryMutation.mutate(
        {
          path: {
            id: library.mediaSource.id,
            libraryId: library.id,
          },
          query: {
            forceScan: force,
          },
        },
        {
          onSuccess: () => {
            queryClient
              .invalidateQueries({
                queryKey: getApiMediaLibrariesByLibraryIdQueryKey({
                  path: { libraryId: library.id },
                }),
                exact: false,
              })
              .catch(console.error);
          },
        },
      );
    },
    [library.id, library.mediaSource.id, queryClient, refreshLibraryMutation],
  );

  const opts = useMemo(
    () =>
      getApiMediaSourcesByMediaSourceIdByLibraryIdStatusOptions({
        path: {
          mediaSourceId: mediaSource.id,
          libraryId: library.id,
        },
      }),
    [library.id, mediaSource.id],
  );

  useQueryObserver(
    opts,
    useCallback(
      (result) => {
        if (result.status !== 'success') {
          return;
        }

        if (!isRefreshing) {
          return;
        }

        if (
          (prevScanState === 'in_progress' || prevScanState === 'queued') &&
          result.data?.state === 'not_scanning'
        ) {
          setIsRefreshing(false);
          setPrevScanState(null);
          queryClient
            .invalidateQueries({
              queryKey: getApiMediaSourcesQueryKey(),
            })
            .catch(console.error);
        } else {
          setPrevScanState(result.data.state);
        }
      },
      [isRefreshing, prevScanState, queryClient],
    ),
  );

  return (
    <>
      <Tooltip
        placement="top"
        title={
          scanStateQuery.data?.state === 'queued'
            ? t`Queued`
            : library.isLocked
              ? t`Scanning`
              : t`Scan`
        }
      >
        <span>
          <IconButton
            disabled={library.isLocked}
            onClick={(e) => startRefresh(e)}
          >
            {scanStateQuery.data?.state === 'queued' ? (
              <HourglassTop />
            ) : (
              <Refresh
                sx={{
                  animation: library.isLocked
                    ? 'spin 2s linear infinite'
                    : undefined,
                }}
              />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {!isRefreshing && (
        <Tooltip
          placement="top"
          title={
            scanStateQuery.data?.state === 'queued'
              ? t`Queued`
              : library.isLocked
                ? t`Scanning`
                : t`Force Scan`
          }
        >
          <span>
            <IconButton
              disabled={library.isLocked}
              onClick={(e) => startRefresh(e, true)}
            >
              <Radar
                sx={{
                  animation: library.isLocked
                    ? 'spin 2s linear infinite'
                    : undefined,
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {isRefreshing && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            minWidth: '40px',
            textAlign: 'center',
          }}
        >
          {`${(scanStateQuery.data?.state === 'in_progress' ? scanStateQuery.data.percentComplete : 0).toFixed(0)}%`}
        </Box>
      )}
    </>
  );
};

const TableName = 'MediaSourceLibraryTable';

export const MediaSourceLibraryTable = () => {
  const { t } = useLingui();
  const dayjs = useDayjs();
  const navigate = useNavigate();
  const { data: mediaSources } = useMediaSources();
  const tableSettings = useStoreBackedTableSettings(TableName);
  const theme = useTheme();
  const smallViewport = useMediaQuery(theme.breakpoints.down('sm'));

  const navigateToLibrary = useCallback(
    (library: MediaSourceLibraryRow) => {
      navigate({
        to:
          library.type === 'local'
            ? '/media_sources/$mediaSourceId'
            : '/media_sources/$mediaSourceId/libraries/$libraryId',
        params: {
          mediaSourceId: library.mediaSource.id,
          libraryId: library.id,
        },
      }).catch(console.error);
    },
    [navigate],
  );

  const columns = useMemo<MRT_ColumnDef<MediaSourceLibraryRow>[]>(() => {
    return [
      {
        header: t`Source Type`,
        id: 'type',
        accessorFn: ({ type }) => capitalize(type),
        // size: 100,
        enableSorting: false,
        grow: false,
      },
      {
        header: t`Name`,
        id: 'name',
        accessorFn(originalRow) {
          if (originalRow.type === 'local') {
            return originalRow.name;
          } else {
            return `${originalRow.mediaSource.name} - ${originalRow.name}`;
          }
        },
        // size: 150,
        // grow: false,
      },
      {
        header: t`Media Type`,
        id: 'mediaType',
        accessorFn: ({ mediaType }) => prettifySnakeCaseString(mediaType),
        // size: 150,
        // grow: false,
      },
      {
        header: t`Last Synced`,
        id: 'lastUpdated',
        accessorFn: ({ lastScannedAt }) =>
          lastScannedAt ? dayjs(lastScannedAt).format('LLL') : '-',
      },
      {
        header: '',
        id: 'actions',
        grow: true,
        enableColumnFilter: false,
        enableColumnActions: false,
        muiTableBodyCellProps: {
          sx: {
            flexDirection: 'row',
          },
          align: 'right',
        },
        Cell: (props) => {
          return (
            <MediaSourceLibraryTableActionCell
              mediaSource={props.row.original.mediaSource}
              library={props.row.original}
            />
          );
        },
      },
    ];
  }, [dayjs, t]);

  const data = useMemo(() => {
    const remoteLibraries = mediaSources
      .filter((source) => source.type !== 'local')
      .flatMap((source) =>
        source.libraries.map((lib) => ({ ...lib, mediaSource: source })),
      )
      .filter((lib) => lib.enabled);
    const localLibraries = mediaSources
      .filter((source) => source.type === 'local')
      .map((source) => {
        return {
          id: 'all',
          enabled: true,
          externalKey: '',
          isLocked: some(source.libraries, (lib) => lib.isLocked),
          mediaSource: source,
          mediaType: source.mediaType,
          type: 'local',
          name: source.name,
          lastScannedAt: maxBy(source.libraries, (lib) => lib.lastScannedAt)
            ?.lastScannedAt,
        } satisfies MediaSourceLibraryRow;
      });

    return [...remoteLibraries, ...localLibraries];
  }, [mediaSources]);

  const renderMobileCards = () => (
    <Stack spacing={1}>
      {data.map((library) => (
        <Card
          key={`${library.mediaSource.id}-${library.id}`}
          variant="outlined"
          onClick={() => navigateToLibrary(library)}
          sx={{ cursor: 'pointer' }}
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {library.type === 'local'
                    ? library.name
                    : `${library.mediaSource.name} \u2013 ${library.name}`}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 0.25,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <NetworkIcon
                      network={library.mediaSource.type}
                      sx={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {prettifySnakeCaseString(library.mediaType)}
                  </Typography>
                  {library.lastScannedAt && (
                    <Typography variant="caption" color="text.secondary">
                      · {dayjs(library.lastScannedAt).format('ll')}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
              >
                <MediaSourceLibraryTableActionCell
                  mediaSource={library.mediaSource}
                  library={library}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  const table = useMaterialReactTable({
    data,
    columns,
    // layoutMode: 'grid',
    // enableRowActions: true,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        grow: true,
        Header: '',
        visibleInShowHideMenu: false,
        muiTableBodyCellProps: {
          sx: {
            flexDirection: 'row',
          },
          align: 'right',
        },
      },
    },
    renderRowActions: ({ row: { original } }) => (
      <MediaSourceLibraryTableActionCell
        mediaSource={original.mediaSource}
        library={original}
      />
    ),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => navigateToLibrary(row.original),
      sx: {
        cursor: 'pointer',
      },
    }),
    positionActionsColumn: 'last',
    ...tableSettings,
    state: {
      ...tableSettings.state,
      columnVisibility: {
        ...tableSettings.state?.columnVisibility,
        type: !smallViewport,
        lastUpdated: !smallViewport,
      },
    },
  });

  return (
    <>
      {smallViewport ? (
        renderMobileCards()
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%' }}>
          <MaterialReactTable table={table} />
        </TableContainer>
      )}
      <Typography variant="body2" align="center">
        <Trans>
          Don't see the library you want here? Ensure it is enabled in the{' '}
          <MuiLink component={Link} to="/settings/sources">
            Media Source Settings.
          </MuiLink>
        </Trans>
      </Typography>
    </>
  );
};
